import type { ReactNode } from "react";
import { LifeBuoy } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import type {
  CustomerRequestCategory,
  KaiCustomerRequestPreview,
} from "./kaiTypes";

const CATEGORY_LABEL: Record<CustomerRequestCategory, string> = {
  bug: "Bug",
  data_issue: "Data issue",
  feature_request: "Feature request",
  configuration: "Configuration",
  billing: "Billing",
  account: "Account",
  other: "Other",
};

const CATEGORY_VARIANT: Record<
  CustomerRequestCategory,
  "default" | "urgent" | "destructive" | "secondary"
> = {
  bug: "destructive",
  data_issue: "urgent",
  feature_request: "default",
  configuration: "secondary",
  billing: "secondary",
  account: "secondary",
  other: "secondary",
};

function categoryBadge(category: string | null | undefined): ReactNode {
  const key = (category ?? "other").toLowerCase() as CustomerRequestCategory;
  const label = CATEGORY_LABEL[key] ?? category ?? "Other";
  const variant = CATEGORY_VARIANT[key] ?? "secondary";
  return <Badge variant={variant}>{label}</Badge>;
}

/**
 * Validate an untyped elicitation preview payload as a `customer_request`.
 * Requires at least a non-empty title or user_message so a placeholder/empty
 * case never renders a confirmation card.
 */
export function parseCustomerRequest(
  payload: unknown,
): KaiCustomerRequestPreview | null {
  if (!payload || typeof payload !== "object") return null;
  const p = payload as Record<string, unknown>;
  const title = typeof p.title === "string" ? p.title.trim() : "";
  const userMessage =
    typeof p.user_message === "string" ? p.user_message.trim() : "";
  if (!title && !userMessage) return null;
  return p as unknown as KaiCustomerRequestPreview;
}

function Field({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[11px] uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <span className="text-sm text-foreground/90">{value}</span>
    </div>
  );
}

/**
 * Structured support-case preview shown inside an elicitation confirmation.
 * Category badge, title, the verbatim user message, and Kai's summary, plus a
 * callout that confirming opens a case with the Twido team and emails them.
 */
export function KaiCustomerRequestCard({
  spec,
}: {
  spec: KaiCustomerRequestPreview;
}) {
  const summary = spec.kai_summary?.trim();
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <LifeBuoy className="size-4 shrink-0 text-primary" aria-hidden />
        {categoryBadge(spec.category)}
      </div>

      {spec.title && <Field label="Title" value={spec.title} />}

      {spec.user_message && (
        <Field
          label="Your request"
          value={<span className="whitespace-pre-wrap">{spec.user_message}</span>}
        />
      )}

      {summary && (
        <Field
          label="Summary"
          value={<span className="whitespace-pre-wrap">{summary}</span>}
        />
      )}

      <div className="rounded-lg border border-amber-500/30 bg-amber-500/8 px-3 py-2 text-xs text-amber-700 dark:text-amber-300">
        Submitting opens a support case with the Twido team, emails them, and
        sends you a confirmation receipt.
      </div>
    </div>
  );
}
